import {
  forwardRef,
  useRef,
  useEffect,
  useImperativeHandle,
  useCallback,
} from 'react'

const MIN_THUMB_HEIGHT = 30
const REACH_THRESHOLD = 1
const SCROLLING_CLASS_TIMEOUT = 300

const PerfectScrollbar = forwardRef(function PerfectScrollbar(
  {
    options,
    children,
    className = '',
    onYReachEnd,
    onYReachStart,
    onScrollUp,
    ...rest
  },
  ref
) {
  const suppressScrollX = !!(options && options.suppressScrollX)

  const containerRef = useRef(null)
  const railRef = useRef(null)
  const thumbRef = useRef(null)

  const mountedRef = useRef(true)
  const lastScrollTopRef = useRef(0)
  const rafRef = useRef(null)
  const scrollingTimeoutRef = useRef(null)
  const dragRef = useRef(null)

  const callbacksRef = useRef({})
  callbacksRef.current = { onYReachEnd, onYReachStart, onScrollUp }

  useImperativeHandle(
    ref,
    () => ({
      get _container() {
        return containerRef.current
      },
    }),
    []
  )

  const updateThumb = useCallback(() => {
    if (!mountedRef.current) return
    const container = containerRef.current
    const rail = railRef.current
    const thumb = thumbRef.current
    if (!container || !rail || !thumb) return
    if (!container.isConnected) return

    const { scrollTop, scrollHeight, clientHeight } = container

    if (scrollTop < lastScrollTopRef.current) {
      lastScrollTopRef.current = scrollTop
    }

    rail.style.top = scrollTop + 'px'

    if (!clientHeight || !scrollHeight || scrollHeight <= clientHeight + 1) {
      container.classList.remove('ps--active-y')
      thumb.style.height = '0px'
      thumb.style.top = '0px'
      return
    }

    container.classList.add('ps--active-y')

    const railHeight = clientHeight
    let thumbHeight = Math.max(
      MIN_THUMB_HEIGHT,
      Math.floor((clientHeight / scrollHeight) * railHeight)
    )
    thumbHeight = Math.min(thumbHeight, railHeight)

    const maxThumbTop = railHeight - thumbHeight
    const maxScrollTop = scrollHeight - clientHeight
    const thumbTop =
      maxScrollTop > 0
        ? Math.max(
            0,
            Math.min(maxThumbTop, (scrollTop / maxScrollTop) * maxThumbTop)
          )
        : 0

    thumb.style.height = thumbHeight + 'px'
    thumb.style.top = thumbTop + 'px'
  }, [])

  const scheduleUpdate = useCallback(() => {
    if (typeof window === 'undefined') return
    if (!mountedRef.current) return
    if (rafRef.current != null) return
    rafRef.current = window.requestAnimationFrame(() => {
      rafRef.current = null
      updateThumb()
    })
  }, [updateThumb])

  const handleScroll = useCallback(() => {
    const container = containerRef.current
    if (!container || !container.isConnected) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const prev = lastScrollTopRef.current
    const delta = scrollTop - prev
    lastScrollTopRef.current = scrollTop

    const cbs = callbacksRef.current

    const safe = (fn) => {
      if (typeof fn !== 'function') return
      try {
        fn()
      } catch (e) {}
    }

    if (delta < 0) {
      safe(cbs.onScrollUp)
    }

    if (scrollTop <= REACH_THRESHOLD) {
      safe(cbs.onYReachStart)
    }

    if (
      scrollHeight > clientHeight &&
      scrollHeight - scrollTop - clientHeight <= REACH_THRESHOLD
    ) {
      safe(cbs.onYReachEnd)
    }

    container.classList.add('ps--scrolling-y')
    if (scrollingTimeoutRef.current != null) {
      clearTimeout(scrollingTimeoutRef.current)
    }
    scrollingTimeoutRef.current = setTimeout(() => {
      const el = containerRef.current
      if (el) el.classList.remove('ps--scrolling-y')
    }, SCROLLING_CLASS_TIMEOUT)

    scheduleUpdate()
  }, [scheduleUpdate])

  const teardownDrag = useCallback(() => {
    const drag = dragRef.current
    if (!drag) return
    dragRef.current = null

    if (typeof window !== 'undefined') {
      window.removeEventListener('pointermove', drag.onMove)
      window.removeEventListener('pointerup', drag.onUp)
      window.removeEventListener('pointercancel', drag.onUp)
    }

    const container = containerRef.current
    if (container) container.classList.remove('ps--clicking')

    if (typeof document !== 'undefined' && document.body) {
      document.body.style.userSelect = ''
    }

    const thumb = thumbRef.current
    if (thumb && thumb.releasePointerCapture && drag.pointerId != null) {
      try {
        if (
          !thumb.hasPointerCapture ||
          thumb.hasPointerCapture(drag.pointerId)
        ) {
          thumb.releasePointerCapture(drag.pointerId)
        }
      } catch (e) {}
    }
  }, [])

  useEffect(() => {
    mountedRef.current = true
    const container = containerRef.current
    if (!container) return undefined

    lastScrollTopRef.current = container.scrollTop || 0

    container.addEventListener('scroll', handleScroll, { passive: true })

    const onWindowResize = () => scheduleUpdate()
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', onWindowResize)
    }

    let resizeObserver = null
    if (typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => scheduleUpdate())
      try {
        resizeObserver.observe(container)
      } catch (e) {}
    }

    let mutationObserver = null
    if (typeof MutationObserver !== 'undefined') {
      mutationObserver = new MutationObserver(() => scheduleUpdate())
      try {
        mutationObserver.observe(container, {
          childList: true,
          subtree: true,
          characterData: true,
        })
      } catch (e) {}
    }

    scheduleUpdate()

    return () => {
      mountedRef.current = false

      container.removeEventListener('scroll', handleScroll)
      if (typeof window !== 'undefined') {
        window.removeEventListener('resize', onWindowResize)
        if (rafRef.current != null) {
          window.cancelAnimationFrame(rafRef.current)
          rafRef.current = null
        }
      }
      if (resizeObserver) {
        try {
          resizeObserver.disconnect()
        } catch (e) {}
      }
      if (mutationObserver) {
        try {
          mutationObserver.disconnect()
        } catch (e) {}
      }
      if (scrollingTimeoutRef.current != null) {
        clearTimeout(scrollingTimeoutRef.current)
        scrollingTimeoutRef.current = null
      }

      teardownDrag()
    }
  }, [handleScroll, scheduleUpdate, teardownDrag])

  const onThumbPointerDown = useCallback(
    (event) => {
      const container = containerRef.current
      const thumb = thumbRef.current
      const rail = railRef.current
      if (!container || !container.isConnected || !thumb || !rail) return

      if (event.button != null && event.button !== 0) return

      event.preventDefault()
      event.stopPropagation()

      teardownDrag()

      const pointerId = event.pointerId != null ? event.pointerId : null

      const onMove = (moveEvent) => {
        const drag = dragRef.current
        const el = containerRef.current
        if (!drag || !el || !el.isConnected) return

        const { scrollHeight, clientHeight } = el
        const railHeight = clientHeight
        const thumbEl = thumbRef.current
        const thumbHeight = thumbEl ? thumbEl.offsetHeight : MIN_THUMB_HEIGHT
        const maxThumbTop = railHeight - thumbHeight
        const maxScrollTop = scrollHeight - clientHeight

        if (maxThumbTop <= 0 || maxScrollTop <= 0) return

        const deltaY = moveEvent.clientY - drag.startY
        const scrollDelta = (deltaY / maxThumbTop) * maxScrollTop
        el.scrollTop = drag.startScrollTop + scrollDelta
      }

      const onUp = () => {
        teardownDrag()
      }

      dragRef.current = {
        startY: event.clientY,
        startScrollTop: container.scrollTop,
        pointerId,
        onMove,
        onUp,
      }

      container.classList.add('ps--clicking')
      if (typeof document !== 'undefined' && document.body) {
        document.body.style.userSelect = 'none'
      }

      if (thumb.setPointerCapture && pointerId != null) {
        try {
          thumb.setPointerCapture(pointerId)
        } catch (e) {}
      }

      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp)
      window.addEventListener('pointercancel', onUp)
    },
    [teardownDrag]
  )

  const containerClassName = [
    'scrollbar-container',
    'ps',
    suppressScrollX ? 'ps--suppress-x' : '',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <div ref={containerRef} className={containerClassName} {...rest}>
      {children}
      <div className="ps__rail-y" ref={railRef}>
        <div
          className="ps__thumb-y"
          ref={thumbRef}
          onPointerDown={onThumbPointerDown}
        />
      </div>
    </div>
  )
})

export default PerfectScrollbar
